import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * Client Candidate Data Service
 * 
 * Handles all API calls for client-specific candidate data
 * This is separate from the master candidate database
 */

const API_BASE = API_URL + '/client/candidates';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const clientCandidateDataService = {

  // ===================================================================
  // COMPLETE PROFILE
  // ===================================================================

  /**
   * Get complete candidate profile (master data + client-specific data)
   * This is the main method to use - returns everything at once
   */
  getCompleteProfile: async (candidateId) => {
    const response = await axios.get(
      `${API_BASE}/${candidateId}/profile`,
      getAuthHeaders()
    );
    return response.data;
  },

  // ===================================================================
  // NOTES
  // ===================================================================

  /**
   * Get client's notes for a candidate
   */
  getNotes: async (candidateId) => {
    const response = await axios.get(
      `${API_BASE}/${candidateId}/notes`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Create or update client's notes for a candidate
   */
  updateNotes: async (candidateId, notes) => {
    const response = await axios.put(
      `${API_BASE}/${candidateId}/notes`,
      { notes },
      getAuthHeaders()
    );
    return response.data;
  },

  // ===================================================================
  // CUSTOM DATA
  // ===================================================================

  /**
   * Get client's custom data for a candidate
   */
  getData: async (candidateId) => {
    const response = await axios.get(
      `${API_BASE}/${candidateId}/data`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Create or update client's custom data for a candidate
   */
  updateData: async (candidateId, data) => {
    const response = await axios.put(
      `${API_BASE}/${candidateId}/data`,
      data,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Mark candidate as needing review (unclean)
   * Used when data is modified in any widget
   */
  markAsUnclean: async (candidateId) => {
    console.log('[clientCandidateDataService.markAsUnclean] Called for candidateId:', candidateId);
    const response = await axios.put(
      `${API_BASE}/${candidateId}/data`,
      { is_clean: false, cleaned_at: null },
      getAuthHeaders()
    );
    console.log('[clientCandidateDataService.markAsUnclean] Response:', response.data);
    return response.data;
  },

  // ===================================================================
  // DOCUMENTS
  // ===================================================================

  /**
   * Get all client-uploaded documents for a candidate
   */
  getDocuments: async (candidateId) => {
    const response = await axios.get(
      `${API_BASE}/${candidateId}/client-documents`,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Upload a new document for a candidate
   */
  createDocument: async (candidateId, documentData) => {
    const response = await axios.post(
      `${API_BASE}/${candidateId}/client-documents`,
      documentData,
      getAuthHeaders()
    );
    return response.data;
  },

  /**
   * Delete a client-uploaded document
   */
  deleteDocument: async (candidateId, documentId) => {
    const response = await axios.delete(
      `${API_BASE}/${candidateId}/client-documents/${documentId}`,
      getAuthHeaders()
    );
    return response.data;
  },

  // ===================================================================
  // STATISTICS
  // ===================================================================

  /**
   * Get statistics about firm's candidate data
   */
  getStats: async () => {
    const response = await axios.get(
      `${API_BASE}/stats`,
      getAuthHeaders()
    );
    return response.data;
  },

};

export default clientCandidateDataService;
